'use client'

import classnames from 'classnames'
import {type PropsWithChildren} from 'react'
import {unreachableCase} from '~/design-system/utils/'
import {useSanityReferrerData} from '~/utils/useSanityReferrerData'
import {type GeneratedPartnerReferrerData} from '~/utils/useSanityReferrerData/types'
import styles from './styles.module.css'
import {generateOfferBanner} from './utils'

const customerReferralPrefix = 'customer-referral-'
export const isCustomerReferrer = (referrerData: GeneratedPartnerReferrerData) =>
  referrerData.frontendKey.startsWith(customerReferralPrefix)

type GenericReferralBannerProps = PropsWithChildren<{
  title?: string
  wrapperClassName?: string
}>

function GenericReferrerBanner(props: GenericReferralBannerProps) {
  const {children, title, wrapperClassName} = props

  if (!children) {
    return null
  }

  return (
    <div className={classnames(styles['generic-referrer-banner'], wrapperClassName)}>
      {title && (
        <>
          <div className={styles['generic-referrer-title']}>{title}</div>
          <div className={styles['generic-referrer-slash']}>/</div>
        </>
      )}
      <div className={styles['generic-referrer-text']}>{children}</div>
    </div>
  )
}

type ReferralPartnerBannerProps = {
  wrapperClassName?: string
}

export default function ReferralPartnerBanner(props: ReferralPartnerBannerProps) {
  const {wrapperClassName} = props
  const data = useSanityReferrerData()
  if (!data) return null
  const {referrerData, partner} = data

  if (!referrerData) return null

  if (partner?.partnerLandingPageInfo?.hideBanner) return null

  if (referrerData.type === 'ycFounder') {
    return (
      <GenericReferrerBanner title="YCombinator" wrapperClassName={wrapperClassName}>
        Deposit your YC investment to get a free 409a valuation and $1,200 cash.
      </GenericReferrerBanner>
    )
  }

  if (referrerData.type === 'generatedPartner') {
    if (isCustomerReferrer(referrerData)) {
      return (
        <GenericReferrerBanner>
          <span>
            <b>
              Start using Mercury to earn up to $250 in cash for you and your
              referrer.
            </b>
            <br />
            If you deposit $10K into your Mercury account within your first 90 days
            of signing up, you{"'"}ll each earn $250.
          </span>
        </GenericReferrerBanner>
      )
    }

    // There is guaranteed to be at least one deal based on the logic in <PartnerReferral />
    return (
      <GenericReferrerBanner title={referrerData.name}>
        {generateOfferBanner(referrerData.offer)}
      </GenericReferrerBanner>
    )
  }

  if (referrerData.type === 'legacyPartner') return null
  unreachableCase(referrerData)
  return null
}
